import { Undo2 } from "lucide-react";
import { Link } from "react-router";

import { stamp } from "@/lib/format";
import { placeLabel } from "@/lib/places";
import type { SpisanieRow } from "@/lib/providers";

/**
 * Таблица списаний — ОДНА на все экраны, где их показывают.
 *
 * Мест три, и вопросы у них разные: раздел «Списание» спрашивает «что списал
 * склад», карточка баркода — «что списали у этого размера», карточка места —
 * «что списали ОТСЮДА». Строка при этом одна и та же, потому что операция одна:
 * та же строка `sSerial_spisanie`, та же серия, та же причина и тот же час.
 * Названная на одном экране одним набором колонок, а на соседнем другим, она
 * читалась бы как два разных события — ровно то, ради чего ленту перемещений
 * рисует один `MoveFeed`.
 *
 * До 11.09.26 таблиц было две: полная в разделе и усечённая копия в карточке
 * баркода. Третья копия приехала бы вместе с карточкой места; вместо этого
 * копии сведены сюда, а различия вынесены в ФЛАГИ — каждый из них отвечает на
 * вопрос «различаются ли строки этим».
 *
 * `withCard` — товар и баркод. Не нужны там, где список отобран одним размером
 * (карточка баркода): столбец одинаковых фотографий — тот же шум, из-за
 * которого номер вещи не показывают в ленте серии.
 *
 * `withPlace` — откуда списали и город. Не нужны в карточке МЕСТА: там все
 * строки об одном месте, и колонка повторяла бы заголовок экрана.
 *
 * `withCategory` — причина. Показывается, когда строки отобраны не по ней: под
 * своим отбором это столбец одинаковых значений.
 *
 * `placeHeader` — как зовётся колонка места. Четвёртый экран — детализация
 * «Оприходования и списания» — показывает и приход, а у прихода место —
 * КУДА вещь вернули: «откуда списали» над ним было бы неправдой.
 *
 * СЕРИЯ ЕСТЬ ВСЕГДА и всегда ссылкой (задача владельца 11.09.26). Списание
 * разбирают от вещи: «какую именно списали» — первый вопрос к строке, а ответ
 * на него открывается карточкой серии.
 */
export function SpisanieFeed({
  rows,
  caption,
  withCard = false,
  withPlace = false,
  withCategory = false,
  placeHeader = "Откуда списали",
}: {
  rows: SpisanieRow[];
  caption: string;
  withCard?: boolean;
  withPlace?: boolean;
  withCategory?: boolean;
  placeHeader?: string;
}) {
  return (
    <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
      <table className="w-full border-collapse text-[13px]">
        <caption className="sr-only">{caption}</caption>
        <thead className="hidden md:table-header-group">
          <tr>
            {withCard ? (
              <>
                <th scope="col" className={TH + " w-[52px] text-left"}>
                  Товар
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Баркод
                </th>
              </>
            ) : null}
            <th scope="col" className={TH + " text-left"}>
              Серия
            </th>
            {withPlace ? (
              <>
                <th scope="col" className={TH + " text-left"}>
                  {placeHeader}
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Город
                </th>
              </>
            ) : null}
            {withCategory ? (
              <th scope="col" className={TH + " text-left"}>
                Причина
              </th>
            ) : null}
            <th scope="col" className={TH + " text-left"}>
              Когда
            </th>
            <th scope="col" className={TH + " text-left"}>
              Кто
            </th>
            <th scope="col" className={TH + " text-left"}>
              Комментарий
            </th>
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {rows.map((row) => (
            <Line
              key={row.spisanie_id ?? `m${row.move_id}`}
              row={row}
              withCard={withCard}
              withPlace={withPlace}
              withCategory={withCategory}
              placeHeader={placeHeader}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TH =
  "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated " +
  "px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg";

function Line({
  row,
  withCard,
  withPlace,
  withCategory,
  placeHeader,
}: {
  row: SpisanieRow;
  withCard: boolean;
  withPlace: boolean;
  withCategory: boolean;
  placeHeader: string;
}) {
  const card = row.card;
  return (
    <tr
      // Строка адресуется НОМЕРОМ СПИСАНИЯ: «третья сверху» не находится в
      // CRM, а список пересортируется при первом же новом списании. У излишка
      // FBS строки списания нет — его адресует номер перемещения.
      data-annot={
        row.spisanie_id !== null
          ? `списание ${row.spisanie_id}`
          : `перемещение ${row.move_id}`
      }
      className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
    >
      {withCard ? (
        <>
          <Cell label="Товар">
            {/* ФОТОГРАФИЯ вместо имени: у пяти разных вещей оно бывает дословно
                одним и тем же («Платье вечернее праздничное сетка»), а человек
                разбирает пропажу, держа вещь или ярлык в руках. Ведёт она в
                карточку ТОВАРА — там все размеры модели, — тогда как баркод
                рядом ведёт в один размер. */}
            {card && card.image_url ? (
              <Link
                to={
                  card.model_id
                    ? `/products/${card.model_id}`
                    : `/barcode/${card.barcode}`
                }
                title={card.name || "Карточка товара"}
                data-annot="фото"
                className="block w-fit"
              >
                <img
                  src={card.image_url}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="h-[54px] w-[40px] rounded-[var(--radius-ui)] border border-border object-cover"
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              </Link>
            ) : (
              <span className="text-fg-muted">
                {card?.model_id ? card.model_id : "—"}
              </span>
            )}
          </Cell>

          <Cell label="Баркод">
            {card && card.barcode ? (
              <span className="flex flex-col">
                <Link
                  to={`/barcode/${card.barcode}`}
                  data-annot="баркод"
                  className="tnum w-fit underline decoration-border underline-offset-2 hover:decoration-fg"
                >
                  {card.barcode}
                </Link>
                {card.size ? (
                  <span className="text-[11px] text-fg-muted">
                    размер {card.size}
                  </span>
                ) : null}
              </span>
            ) : (
              // Вещь, не заведённую на маркетплейс, называет только серия. Это
              // обычное дело, а не сбой: до маркетплейса вещи заводили без
              // карточки.
              <span className="text-fg-muted">не на маркетплейсе</span>
            )}
          </Cell>
        </>
      ) : null}

      <Cell label="Серия">
        <Link
          to={`/serial/${row.serial}`}
          data-annot="серия"
          className="tnum underline decoration-border underline-offset-2 hover:decoration-fg"
        >
          {row.serial}
        </Link>
      </Cell>

      {withPlace ? (
        <>
          <Cell label={placeHeader}>
            {row.place && row.place.place_id ? (
              <span className="flex flex-col">
                <Link
                  to={`/place/${row.place.place_id}`}
                  className="w-fit underline decoration-border underline-offset-2 hover:decoration-fg"
                >
                  {/* Короб зовут НОМЕРОМ, прочие места — именем: общее правило
                      кабинета. Имя короба в CRM городу не равно и часто ему
                      противоречит — «Невиномыск Невиномыск Короб 2779» стоит в
                      Ростове, а город стоит колонкой рядом. */}
                  {placeLabel({
                    place_id: row.place.place_id,
                    name: row.place.name,
                    code: row.place.code,
                    korob: row.place.korob,
                  })}
                </Link>
                {row.place.group ? (
                  <span className="text-[11px] text-fg-muted">
                    {row.place.group}
                  </span>
                ) : null}
              </span>
            ) : (
              // Перемещения у строки нет — у старых записей его не бывает
              // вовсе. «Откуда не знаем» честнее пустой ячейки.
              <span
                className="text-fg-muted"
                title="У этой строки нет перемещения"
              >
                неизвестно
              </span>
            )}
          </Cell>

          <Cell label="Город">
            <span className="text-fg-muted">{row.place?.city || "—"}</span>
          </Cell>
        </>
      ) : null}

      {withCategory ? (
        <Cell label="Причина">{row.category || row.category_id}</Cell>
      ) : null}

      <Cell label="Когда" align="whitespace-nowrap">
        <span className="flex flex-col">
          <span className="tnum">{stamp(row.at) || "—"}</span>
          {/* ВОЗВРАТ В ОБОРОТ — та же таблица CRM и та же операция, но
              обратная. Молчание значит «списали»: подпись у каждой строки была
              бы шумом. */}
          {row.available === 1 ? (
            <span className="flex items-center gap-1 text-[11px] text-fg-muted">
              <Undo2 size={11} className="flex-none" />
              возврат в оборот
            </span>
          ) : null}
        </span>
      </Cell>

      <Cell label="Кто">
        <span className="text-fg-muted">{row.user || "—"}</span>
      </Cell>

      <Cell label="Комментарий">
        {/* Комментарий бывает длиной в предложение («FBS WB: задание …,
            поставка … от …»), и в колонке он читается началом: целиком — в
            подсказке, чтобы не рвать строку таблицы на три. */}
        <span
          title={row.comment ?? ""}
          className="block break-words text-fg-muted md:max-w-[320px] md:truncate"
        >
          {row.comment || "—"}
        </span>
      </Cell>
    </tr>
  );
}

/**
 * Ячейка, которая на узком экране становится строкой «подпись — значение».
 * Тот же приём, что во всех таблицах кабинета.
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}
